import type { FormField, FormSchema } from "@/lib/database";

/**
 * Динамическая группа полей с ИИ (field-group + модификация через ИИ).
 *
 * Группа с settings.aiEnabled === true генерирует дополнительные поля через
 * POST /api/forms/[id]/ai-next на шаге 2+ (на шаге 1 генерация не выполняется —
 * тоггл в конструкторе задизеблен). Статичные поля группы отображаются всегда,
 * динамические добавляются в рантайме в settings.groupFields.
 *
 * Спецификация: kanban projme t_b8ffa1fc.
 */

export interface AiGeneratedField {
	id: string;
	label: string;
	options?: string[];
	required?: boolean;
	tag: string;
	type: string;
}

/** Значения динамических полей сессии: groupFieldId -> { fieldId -> value } */
export type AiDynamicValues = Record<string, Record<string, any>>;

export interface AiSubmitMeta {
	/** Пометка деградации: ИИ не сработал, отправлены только статичные поля */
	ai_degraded?: boolean;
	/** ISO-время генерации динамических полей */
	generated_at?: string;
}

const MAX_DYNAMIC_FIELDS = 10;

/**
 * Словарь тегов, доступных LLM. Ключ = tag, значение = реальный type поля
 * рендерера (проверяется в fieldFactory). Всё вне словаря сервер отбрасывает.
 */
const TAG_TO_FIELD_TYPE: Record<string, string> = {
	text: "text",
	long_text: "textarea",
	email: "email",
	number: "number",
	phone: "phone",
	link: "link",
	date: "date",
	time: "time",
	rating: "rating",
	single_choice: "radio",
	multi_choice: "checkbox",
	dropdown: "select",
	address: "address",
};

/** Для каких типов LLM генерирует options */
const TAGS_WITH_OPTIONS = new Set([
	"single_choice",
	"multi_choice",
	"dropdown",
]);

export const isAiGroup = (field: FormField): boolean =>
	field.type === "field-group" && field.settings?.aiEnabled === true;

export const getGroupFields = (field: FormField): FormField[] =>
	(field.settings?.groupFields as FormField[] | undefined) || [];

/**
 * Найти AI-группу в схеме (первую; несколько не поддерживается — сервер
 * использует только первую, конструктор не запрещает больше).
 */
export const findAiGroup = (schema: FormSchema): FormField | null => {
	const blocks = schema.blocks || [];
	const flat = schema.fields || [];
	for (const block of blocks) {
		for (const field of block.fields || []) {
			if (isAiGroup(field)) {
				return field;
			}
		}
	}
	for (const field of flat) {
		if (isAiGroup(field)) {
			return field;
		}
	}
	return null;
};

/** 0-based индекс блока, содержащего поле (blocks-режим). */
export const findFieldStepIndex = (
	schema: FormSchema,
	fieldId: string
): number => {
	const blocks = schema.blocks || [];
	for (let i = 0; i < blocks.length; i++) {
		if (blocks[i].fields?.some((f) => f.id === fieldId)) {
			return i;
		}
	}
	return -1;
};

export const isAiGroupOnStepTwoPlus = (
	schema: FormSchema,
	groupField: FormField
): boolean => {
	const stepIndex = findFieldStepIndex(schema, groupField.id);
	return stepIndex >= 1;
};

/**
 * Собрать список полей для отправки в ai-next (valuesSoFar): id + label + type
 * всех видимых/заполненных контролов вне AI-группы. Порядок = порядок блоков.
 */
export const collectFieldsForPrompt = (
	schema: FormSchema,
	aiGroupId: string
): { id: string; label: string; type: string }[] => {
	const result: { id: string; label: string; type: string }[] = [];
	const push = (field: FormField) => {
		if (field.id === aiGroupId) {
			return;
		}
		if (
			field.type === "statement" ||
			field.type === "banner" ||
			field.type === "signature" ||
			field.type === "file"
		) {
			return;
		}
		result.push({
			id: field.id,
			label: field.label || field.id,
			type: field.type,
		});
	};
	for (const block of schema.blocks || []) {
		for (const field of block.fields || []) {
			push(field);
		}
	}
	for (const field of schema.fields || []) {
		push(field);
	}
	return result;
};

/** Обрезать valuesSoFar до лимита (санитайзинг объёма промпта). */
export const capValuesSoFar = (
	valuesSoFar: Record<string, any>,
	maxChars = 8000
): Record<string, any> => {
	try {
		const json = JSON.stringify(valuesSoFar);
		if (json.length <= maxChars) {
			return valuesSoFar;
		}
		// Жёсткое усечение: берём первые записи, пока не выйдет за лимит
		const capped: Record<string, any> = {};
		for (const [key, value] of Object.entries(valuesSoFar)) {
			capped[key] = value;
			if (JSON.stringify(capped).length > maxChars) {
				delete capped[key];
				break;
			}
		}
		return capped;
	} catch {
		return {};
	}
};

export const makeDynamicFieldId = (groupFieldId: string): string =>
	`${groupFieldId}_dyn_${Date.now().toString(36)}_${Math.random()
		.toString(36)
		.slice(2, 7)}`;

/** Стабильный идентификатор AI-группы для внешних систем агрегации. */
export const makeAiGroupId = (): string =>
	`grp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;

/** Нормализовать ответ LLM в валидные поля; мусор отбрасывается. */
export const normalizeAiFields = (raw: unknown): AiGeneratedField[] => {
	if (!Array.isArray(raw)) {
		return [];
	}
	const out: AiGeneratedField[] = [];
	for (const item of raw) {
		if (!item || typeof item !== "object") {
			continue;
		}
		const obj = item as Record<string, unknown>;
		const tag = typeof obj.tag === "string" ? obj.tag : "";
		const type = TAG_TO_FIELD_TYPE[tag];
		if (!type) {
			continue;
		}
		const label = typeof obj.label === "string" ? obj.label.trim() : "";
		if (!label || label.length > 200) {
			continue;
		}
		if (out.length >= MAX_DYNAMIC_FIELDS) {
			break;
		}
		const field: AiGeneratedField = {
			id: makeDynamicFieldId("dyn"),
			type,
			label,
			tag,
		};
		if (typeof obj.required === "boolean") {
			field.required = obj.required;
		}
		if (TAGS_WITH_OPTIONS.has(tag) && Array.isArray(obj.options)) {
			const options = obj.options
				.filter(
					(o): o is string => typeof o === "string" && o.trim().length > 0
				)
				.map((o) => o.trim().slice(0, 100))
				.slice(0, 20);
			if (options.length >= 2) {
				field.options = options;
			}
		}
		out.push(field);
	}
	return out;
};

export const validateAiFieldsResponse = (fields: AiGeneratedField[]): boolean =>
	fields.length > 0;

/**
 * Влить сгенерированные поля в settings.groupFields группы.
 * Мутирует settings целевой группы в schema (поверхностное клонирование
 * затронутых уровней: blocks/fields/settings). Возвращает новую schema.
 */
export const mergeAiFieldsIntoSchema = (
	schema: FormSchema,
	groupFieldId: string,
	fields: AiGeneratedField[]
): FormSchema => {
	const cloneField = (field: FormField): FormField => {
		if (field.id !== groupFieldId) {
			return field;
		}
		return {
			...field,
			settings: {
				...field.settings,
				groupFields: [
					...getGroupFields(field),
					...fields.map(
						(f) => ({ ...f, type: f.type }) as unknown as FormField
					),
				],
			},
		};
	};
	return {
		...schema,
		blocks: schema.blocks?.map((block) => ({
			...block,
			fields: block.fields?.map(cloneField),
		})),
		fields: schema.fields?.map(cloneField),
	} as FormSchema;
};

/**
 * Заменить динамические поля группы (при повторной генерации/откате).
 * Статичные поля (без суффикса _dyn_) сохраняются.
 */
export const replaceAiDynamicFields = (
	schema: FormSchema,
	groupFieldId: string,
	fields: AiGeneratedField[]
): FormSchema => {
	const cloneField = (field: FormField): FormField => {
		if (field.id !== groupFieldId) {
			return field;
		}
		const staticFields = getGroupFields(field).filter(
			(f) => !f.id.includes("_dyn_")
		);
		return {
			...field,
			settings: {
				...field.settings,
				groupFields: [
					...staticFields,
					...fields.map((f) => ({ ...f }) as unknown as FormField),
				],
			},
		};
	};
	return {
		...schema,
		blocks: schema.blocks?.map((block) => ({
			...block,
			fields: block.fields?.map(cloneField),
		})),
		fields: schema.fields?.map(cloneField),
	} as FormSchema;
};

/** Динамические (сгенерированные) поля группы. */
export const getAiDynamicFields = (field: FormField): FormField[] =>
	getGroupFields(field).filter((f) => f.id.includes("_dyn_"));

/** Статичные поля группы (добавленные владельцем в конструкторе). */
export const getAiStaticFields = (field: FormField): FormField[] =>
	getGroupFields(field).filter((f) => !f.id.includes("_dyn_"));

export interface AiSubmitEntry {
	label: string;
	tag: string;
	type: string;
	value: any;
}

/**
 * Трансформация при отправке: значение группы (объект subgroupId -> value)
 * разворачивается в отдельные записи submission_data с метаданными для
 * владельца и будущей агрегации по tag. Статичные поля группы тоже
 * разворачиваются (их label теперь доступен без лукапа по схеме).
 */
export const transformAiGroupValueForSubmit = (
	groupField: FormField,
	groupValue: Record<string, any> | undefined,
	dynamicIds: Set<string>
): Record<string, AiSubmitEntry> => {
	const out: Record<string, AiSubmitEntry> = {};
	if (!groupValue || typeof groupValue !== "object") {
		return out;
	}
	for (const field of getGroupFields(groupField)) {
		if (!(field.id in groupValue)) {
			continue;
		}
		const raw = groupValue[field.id];
		const isEmpty =
			raw === undefined ||
			raw === null ||
			raw === "" ||
			(Array.isArray(raw) && raw.length === 0);
		if (isEmpty && !dynamicIds.has(field.id)) {
			continue;
		}
		out[field.id] = {
			label: field.label || field.id,
			type: field.type,
			tag: dynamicIds.has(field.id)
				? (field as any).tag || field.type
				: field.type,
			value: raw,
		};
	}
	return out;
};

/**
 * Пометка деградации: включается, если AI-группа есть в схеме, но генерация
 * не удалась/пропущена. Хранится в submission_data под ключом __ai_meta
 * (двойное подчёркивание = служебный, в аналитике/модалке скипается).
 */
export const buildAiMetaEntry = (
	degraded: boolean,
	generatedAt?: string
): AiSubmitMeta | null => {
	if (!(degraded || generatedAt)) {
		return null;
	}
	const meta: AiSubmitMeta = {};
	if (degraded) {
		meta.ai_degraded = true;
	}
	if (generatedAt) {
		meta.generated_at = generatedAt;
	}
	return meta;
};

export const AI_META_KEY = "__ai_meta";
